import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Users, Search, Check, X } from "lucide-react";

/**
 * Multi-select of user names (used for Tim Pendamping).
 * value: string[] of names
 * onChange: (names: string[]) => void
 * users: [{id, name, username, role}]
 * exclude: string (name to hide from selection, e.g. current PIC)
 */
export default function MultiUserPicker({ value = [], onChange, users = [], exclude, disabled = false, testid = "tim" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users
      .filter((u) => u.name && u.name !== exclude)
      .filter((u) => !s || u.name.toLowerCase().includes(s) || u.username.toLowerCase().includes(s));
  }, [q, users, exclude]);

  const toggle = (name) => {
    if (value.includes(name)) onChange(value.filter((n) => n !== name));
    else onChange([...value, name]);
  };

  return (
    <div data-testid={`${testid}-picker`}>
      <div className="flex flex-wrap gap-1.5 items-center min-h-[38px] p-1.5 rounded-md border border-[#E5E7EB] bg-white">
        {value.length === 0 && (
          <span className="text-xs text-[#9CA3AF] px-1">
            {disabled ? "—" : "Belum ada tim pendamping"}
          </span>
        )}
        {value.map((n) => (
          <span
            key={n}
            className="inline-flex items-center gap-1 bg-[#0055FF]/10 text-[#0044CC] text-xs font-semibold px-2 py-1 rounded-md"
            data-testid={`${testid}-chip-${n}`}
          >
            {n}
            {!disabled && (
              <button
                type="button"
                onClick={() => toggle(n)}
                className="text-[#0044CC] hover:text-[#991B1B]"
                data-testid={`${testid}-remove-${n}`}
                aria-label={`Hapus ${n}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`${testid}-add`}
                className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md border border-dashed border-[#E5E7EB] hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
              >
                <Users className="h-3 w-3" /> Tambah
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end" data-testid={`${testid}-panel`}>
              <div className="p-2 border-b border-[#E5E7EB]">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Cari nama..."
                    className="w-full h-8 pl-7 pr-2 rounded border border-[#E5E7EB] text-xs focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent"
                    data-testid={`${testid}-search`}
                  />
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {list.length === 0 && (
                  <div className="px-3 py-4 text-center text-xs text-[#9CA3AF]">Tidak ada hasil</div>
                )}
                {list.map((u) => {
                  const active = value.includes(u.name);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggle(u.name)}
                      data-testid={`${testid}-opt-${u.username}`}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[#F8F9FA] ${active ? "bg-[#0055FF]/[0.06]" : ""}`}
                    >
                      <div className="flex flex-col items-start min-w-0">
                        <span className="font-semibold text-[#0A0A0A] truncate">{u.name}</span>
                        <span className="text-[10px] text-[#9CA3AF] font-mono-tab uppercase tracking-wider">
                          {u.role} · {u.username}
                        </span>
                      </div>
                      {active && <Check className="h-3.5 w-3.5 text-[#0055FF] flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
